import { getHttpClient } from '@vnetwork/margarita-core/http';
import {
  defineWorkspaceService,
  normalizeId,
  sameId,
  splitLoc,
  type OrgWorkspace,
  type RouteMatch,
  type WorkspaceClient,
} from '@vnetwork/margarita-shell/iam';
import { K8S_V2_BASE } from '../paths';
import { K8S_WORKSPACE_KEY, readK8sWorkspace } from './workspace-answer';
import { organizationParam, workspaceSearch } from './workspace-org';

const SHARED_KEY = ['k8s2', 'workspaces-shared'] as const;

/** The service type the registry and `resolveOrganizationWorkspace` know Kubernetes by. */
const KUBERNETES_WORKSPACE_TYPE = 'kubernetes';

interface HomeWorkspace {
  name?: string;
  organization_id: string;
}

/** One item of GET /api/v2/workspaces/shared (kubernetes internal/api/iam_shared.go sharedWorkspaceItem). */
interface SharedItem {
  organization_id: string;
  name?: string;
  org_wide?: boolean;
  project_ids?: string[];
  cluster_ids?: string[];
}

/** Both answers name their resource under its own key and carry no envelope (`_shared.ts`). */
const field = <T>(body: unknown, key: string): T | undefined =>
  body && typeof body === 'object' ? ((body as Record<string, unknown>)[key] as T | undefined) : undefined;

function sharedEntries(items: readonly SharedItem[]): OrgWorkspace[] {
  return items.map((w) => {
    const projectIds = w.project_ids ?? [];
    const resourceIds = w.cluster_ids ?? [];
    return {
      serviceType: KUBERNETES_WORKSPACE_TYPE,
      organizationId: normalizeId(w.organization_id),
      organizationName: w.name ?? '',
      home: false,
      // The workspace is named by the `organization_id` search parameter, not by a path segment: nothing for `ref`.
      ref: null,
      scope: w.org_wide ? 'org' : projectIds.length > 0 ? 'projects' : 'resources',
      projectIds,
      resourceIds,
    };
  });
}

/** The pages that exist in every workspace. VPCs read the caller's own organization only, so they are not among them. */
const KUBERNETES_PAGES: readonly string[] = ['clusters'];

const clustersHref = (ws: OrgWorkspace) => `${K8S_V2_BASE}/clusters${workspaceSearch(ws.organizationId, ws.home)}`;

/**
 * `/services/kubernetes/v2/<page>/...` and the workspace it is in: `?organization_id=` names a shared one, no parameter
 * (or the home organization) is the home one. `null` for another service's URL, for the v1 console, and for an
 * organization that is not among the known workspaces (no Kubernetes tenant, or nothing shared).
 */
export function matchKubernetesRoute(loc: string, known: readonly OrgWorkspace[]): RouteMatch | null {
  const { pathname, search } = splitLoc(loc);
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'services' || segments[1] !== 'kubernetes' || segments[2] !== 'v2') return null;
  const home = known.find((w) => w.home);
  const named = organizationParam(search);
  const workspace = !named || sameId(named, home?.organizationId) ? home : known.find((w) => !w.home && sameId(w.organizationId, named));
  if (!workspace) return null;
  return { organizationId: workspace.organizationId, page: segments[3] ?? '', detail: segments.length > 4 };
}

/**
 * @param options.enabled the Kubernetes v2 API is configured (`APP_K8S_API_URL`). Without it the adapter answers
 * nothing without a request and is not switchable, so the top bar never calls an origin that is not Kubernetes.
 */
export function createKubernetesWorkspaceService(client: WorkspaceClient, options: { enabled: boolean } = { enabled: true }) {
  return defineWorkspaceService<HomeWorkspace | null, SharedItem[]>({
    type: KUBERNETES_WORKSPACE_TYPE,
    // Same key and answer as `useWorkspace`, the console's gate: one fetch serves both.
    home: {
      queryKey: K8S_WORKSPACE_KEY,
      staleTime: 60_000,
      fetch: async () => (options.enabled ? readK8sWorkspace<HomeWorkspace>((await client.get('/workspace')).data) : null),
    },
    shared: {
      queryKey: SHARED_KEY,
      staleTime: 5 * 60_000,
      fetch: async () => {
        if (!options.enabled) return [];
        const items = field<SharedItem[]>((await client.get('/workspaces/shared')).data, 'workspaces');
        return Array.isArray(items) ? items : [];
      },
    },
    // An organization with no Kubernetes tenant has no workspace (`workspace: null`) and is simply not listed.
    homeEntries: (ws) =>
      ws?.organization_id
        ? [
            {
              serviceType: KUBERNETES_WORKSPACE_TYPE,
              organizationId: normalizeId(ws.organization_id),
              organizationName: ws.name ?? '',
              home: true,
              ref: null,
              scope: 'org',
              projectIds: [],
              resourceIds: [],
            },
          ]
        : [],
    sharedEntries,
    // The workspace is the `organization_id` search parameter (absent = the home one); the cluster list is the console's base.
    href: (ws, page) => (page && page !== 'clusters' ? `${K8S_V2_BASE}/${page}${workspaceSearch(ws.organizationId, ws.home)}` : clustersHref(ws)),
    matchRoute: matchKubernetesRoute,
    pages: KUBERNETES_PAGES,
    dashboard: clustersHref,
    landing: K8S_V2_BASE,
    switchable: options.enabled,
  });
}

// The client is looked up per call, not at import: this file is in the entry chunk (module.ts) and the registry is filled at boot.
const k8sClient: WorkspaceClient = { get: (url) => getHttpClient('k8sV2Api').get(url) };

/** The module's adapter; `enabled` is `v2Enabled` of `createKubernetesModule`. */
export const workspaceServiceFor = (enabled: boolean) => createKubernetesWorkspaceService(k8sClient, { enabled });
